import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { blogArticles } from '@/mocks/seo-content';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isHomeTop = !scrolled && location.pathname === '/';

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function closeAll() {
    setDropdownOpen(false);
    setGuideDropdownOpen(false);
    setMobileMenuOpen(false);
  }

  const textColor = isHomeTop
    ? 'text-background-50'
    : 'text-foreground-900';
  const textHoverColor = isHomeTop
    ? 'hover:text-background-50/80'
    : 'hover:text-foreground-700';
  const pillBase = isHomeTop
    ? 'border-background-50/30 bg-background-50/10 text-background-50 hover:bg-background-50/20'
    : 'border-background-300/60 bg-background-50 text-foreground-700 hover:border-foreground-300';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 h-14 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : location.pathname === '/'
          ? 'bg-black/25 backdrop-blur-sm'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
      }`}
    >
      <div className="w-full h-full flex items-center justify-between px-4 md:px-6">
        {/* Logo */}
        <Link
          to="/"
          onClick={closeAll}
          className={`font-heading text-lg font-semibold tracking-tight whitespace-nowrap transition-colors cursor-pointer ${textColor} ${textHoverColor}`}
        >
          pandabuy.digital
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-2">
          {/* Home */}
          <Link
            to="/"
            className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              isHomeTop
                ? 'text-background-50/85 hover:text-background-50 hover:bg-background-50/10'
                : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
            }`}
          >
            Home
          </Link>

          {/* Category Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => { setDropdownOpen(!dropdownOpen); setGuideDropdownOpen(false); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${pillBase}`}
            >
              <span>Browse Categories</span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 border border-background-200/70 rounded-2xl shadow-lg overflow-hidden z-50 backdrop-blur-xl">
                <div className="grid grid-cols-2 gap-1 p-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Shopping Guide Dropdown */}
          <div className="relative" ref={guideDropdownRef}>
            <button
              onClick={() => { setGuideDropdownOpen(!guideDropdownOpen); setDropdownOpen(false); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${pillBase}`}
            >
              <span>Shopping Guide</span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </span>
            </button>

            {guideDropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-80 bg-background-50 border border-background-200/70 rounded-2xl shadow-lg overflow-hidden z-50 backdrop-blur-xl">
                <div className="p-2 max-h-96 overflow-y-auto space-y-1">
                  {blogArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/blog/${article.slug}`}
                      onClick={() => setGuideDropdownOpen(false)}
                      className="flex items-start gap-3 px-3 py-3 rounded-xl text-sm hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5 text-accent-500">
                        <i className="ri-article-line text-sm"></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-foreground-800 font-medium group-hover:text-foreground-950 transition-colors leading-snug line-clamp-2">
                          {article.title}
                        </p>
                        <p className="text-xs text-foreground-400 mt-1">{article.category}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pandabuy Sheet CTA */}
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5"
          >
            Pandabuy Sheet
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-xs"></i>
            </span>
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer"
          style={{ color: isHomeTop ? '#fff' : undefined }}
          aria-label="Toggle menu"
        >
          <span className={`w-5 h-5 flex items-center justify-center ${isHomeTop ? 'text-background-50' : 'text-foreground-700'}`}>
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-3">
            {/* Quick Links */}
            <div className="flex gap-2">
              <Link
                to="/"
                onClick={closeAll}
                className="flex-1 text-center px-3 py-2.5 rounded-full bg-background-100 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                Home
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeAll}
                className="flex-1 text-center px-3 py-2.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Pandabuy Sheet
              </a>
            </div>

            {/* Buy It Now */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer"
            >
              Buy It Now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-sm"></i>
              </span>
            </a>

            {/* Categories Grid */}
            <p className="text-xs text-foreground-400 font-semibold uppercase tracking-wider pt-2">Categories</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeAll}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-accent-500 flex-shrink-0">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            {/* Shopping Guide Section */}
            <p className="text-xs text-foreground-400 font-semibold uppercase tracking-wider pt-2">Shopping Guide</p>
            <div className="space-y-1">
              {blogArticles.slice(0, 5).map((article) => (
                <Link
                  key={article.slug}
                  to={`/blog/${article.slug}`}
                  onClick={closeAll}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-accent-500">
                    <i className="ri-article-line text-sm"></i>
                  </span>
                  <span className="truncate">{article.title}</span>
                </Link>
              ))}
              {blogArticles.length > 5 && (
                <p className="px-3 py-1 text-xs text-foreground-400">
                  + {blogArticles.length - 5} more articles available on desktop
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}